import { useEffect, useRef } from 'react';
import { BASE_URL, refreshAccessToken } from '../auth/apiClient';

const SOCKET_URL = BASE_URL.replace(/^http/, 'ws') + 'ws/lab/';
const CLOSE_UNAUTHORIZED = 4401;
const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;
const FALLBACK_RESYNC_MS = 60000;

// Subscribes to the per-user lab socket. Server messages are hints only: callers
// re-fetch over HTTP in onEvent(message) and onResync(). onResync runs after every
// successful (re)authentication, and every 60 s while the socket is not authenticated.
const useLabSocket = ({ onEvent, onResync }) => {
    const onEventRef = useRef(onEvent);
    const onResyncRef = useRef(onResync);

    useEffect(() => {
        onEventRef.current = onEvent;
        onResyncRef.current = onResync;
    });

    useEffect(() => {
        let socket = null;
        let reconnectTimer = null;
        let fallbackTimer = null;
        let backoffMs = INITIAL_BACKOFF_MS;
        // Only one token refresh per authentication, so a server that keeps
        // rejecting fresh tokens gets the backoff instead of a tight loop.
        let refreshedSinceAuth = false;
        let unmounted = false;

        const startFallback = () => {
            if (!fallbackTimer) {
                fallbackTimer = setInterval(() => onResyncRef.current(), FALLBACK_RESYNC_MS);
            }
        };

        const stopFallback = () => {
            clearInterval(fallbackTimer);
            fallbackTimer = null;
        };

        const scheduleReconnect = () => {
            reconnectTimer = setTimeout(connect, backoffMs);
            backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
        };

        const connect = () => {
            reconnectTimer = null;
            const ws = new WebSocket(SOCKET_URL);
            socket = ws;

            ws.onopen = () => {
                const currentUser = JSON.parse(localStorage.getItem('user'));
                ws.send(JSON.stringify({ type: 'auth', token: currentUser && currentUser.access }));
            };

            ws.onmessage = (event) => {
                let message;
                try {
                    message = JSON.parse(event.data);
                } catch (error) {
                    return;
                }
                if (message.type === 'auth.ok') {
                    backoffMs = INITIAL_BACKOFF_MS;
                    refreshedSinceAuth = false;
                    stopFallback();
                    onResyncRef.current();
                } else if (message.type === 'analysis.changed') {
                    onEventRef.current(message);
                }
            };

            // An error event is always followed by close, so reconnecting here covers both.
            ws.onclose = (event) => {
                if (unmounted) return;
                socket = null;
                startFallback();

                if (event.code !== CLOSE_UNAUTHORIZED || refreshedSinceAuth) {
                    scheduleReconnect();
                    return;
                }
                refreshedSinceAuth = true;
                refreshAccessToken()
                    .then(() => {
                        if (!unmounted) connect();
                    })
                    .catch(() => {
                        localStorage.removeItem('user');
                        window.location.href = '#/login';
                    });
            };
        };

        startFallback();
        connect();

        return () => {
            unmounted = true;
            clearTimeout(reconnectTimer);
            stopFallback();
            if (socket) socket.close();
        };
    }, []);
};

export default useLabSocket;
